import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, WORKER_API_BASE, iframeClientParam } from '../lib/api';
import { Plus, Search, Trash2, Download, Pencil, AlertTriangle, Info, Copy, Receipt, CornerUpRight, Link2, Zap, History, FileText, MoreVertical, ExternalLink } from 'lucide-react';
import { tr } from '../lib/i18nHelpers';
import InvoiceNumberBadge from '../components/InvoiceNumberBadge';
import { promptReviewedVariance, promptUndoReason } from '../lib/matching';
import { useDateFilter } from '../contexts/DateFilterContext';
import { useToast } from '../components/Toast';
import AutoMatchReviewModal from '../components/AutoMatchReviewModal';
import LLMMatchModal from '../components/LLMMatchModal';
import AuditTrailModal from '../components/AuditTrailModal';
import InvoiceDetailPanel from '../components/InvoiceDetailPanel';
import InvoicePostingEditor from '../components/InvoicePostingEditor';
import SlideOpen from '../components/SlideOpen';
import Tooltip from '../components/Tooltip';
import LifecycleStepper from '../components/LifecycleStepper';
import LifecycleDrilldown from '../components/LifecycleDrilldown';
import { stageOf } from '../lib/lifecycleStepper';
import { ReceiptMatchReviewModal } from './AP';
import { useHighlightTarget } from '../hooks/useHighlightTarget';
import { isTemporaryAccount } from '../lib/coa-hierarchy';
import { useTableControls, Th, TablePager, TABLE_FETCH_CAP, ColumnMeta } from '../components/table';

const COLS: ColumnMeta[] = [
  { key: 'invoice_number', type: 'text' },
  { key: '_counterparty', type: 'text' },
  { key: 'status', type: 'text' },
  { key: 'total', type: 'number' },
  { key: 'issue_date', type: 'date' },
];

// Authenticated PDF download: fetches with Bearer token, opens as blob URL
async function downloadInvoicePDF(invoiceId: string, invoiceNumber: string) {
  const token = localStorage.getItem('token') || '';
  const activeClientJson = localStorage.getItem('activeClient');
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  try {
    const clientObj = activeClientJson ? JSON.parse(activeClientJson) : null;
    if (clientObj?.id) headers['X-Active-Client'] = clientObj.id;
  } catch {}
  try {
    const res = await fetch(`${WORKER_API_BASE}/pdf/invoice/${invoiceId}`, { headers });
    if (!res.ok) { alert('PDF generation failed — please try again.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Invoice_${invoiceNumber}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch {
    alert('Could not download PDF. Please check your connection.');
  }
}

export default function AR() {
  const { i18n } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [viewId, setViewId] = useState<string | null>(null);
  const [drillOpen, setDrillOpen] = useState(false);
  const [auditId, setAuditId] = useState<string | null>(null);
  // Inline posting editor under a row — opened by clicking its Dr/Cr chips
  const [postingEditId, setPostingEditId] = useState<string | null>(null);
  const [receiptMatchResults, setReceiptMatchResults] = useState<any[] | null>(null);
  const [bankMatchResults, setBankMatchResults] = useState<any[] | null>(null);
  const [showLLMModal, setShowLLMModal] = useState(false);
  const [llmModalType, setLlmModalType] = useState<'bank-invoice' | 'receipt-invoice'>('receipt-invoice');
  // useAI removed 2026-11-05: dead toggle (never switchable) gating the match buttons to the weaker LLM pipeline
  const [form, setForm] = useState({ invoice_number: '', customer_id: '', issue_date: new Date().toISOString().split('T')[0], due_date: '', receipt_number: '', paid_date: '', currency: 'HKD', tax_rate: 0, discount_amount: 0, discount_type: 'flat' as string, discount_value: 0, notes: '', terms: '', attn: '', customer_phone: '', customer_email: '', customer_address: '', items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }] });
  const [productSearch, setProductSearch] = useState<Record<number, string>>({});
  const [productDropdown, setProductDropdown] = useState<number | null>(null);
  const [addProductForm, setAddProductForm] = useState({ name: '', unit_price: 0 });
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, right: 0 });
  const { startDate, endDate } = useDateFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const { highlight: stateHighlight } = useHighlightTarget();
  // Nov-4 item 2: the import-notice banner and File Storage's "Go to AR record"
  // deep-link via ?highlight= (URL survives reload + new tabs) — merge with the
  // state-based highlight target (mirrors Invoices.tsx).
  const highlightId = stateHighlight || searchParams.get('highlight') || '';
  // Deep-link highlight bypasses the fiscal-year date filter so the invoice is always found.
  const effStart = highlightId ? '' : startDate;
  const effEnd = highlightId ? '' : endDate;

  const { data, isLoading } = useQuery({
    queryKey: ['invoices-ar', search, status, effStart, effEnd, highlightId],
    queryFn: () => {
      const params = new URLSearchParams({ q: highlightId ? '' : search, status: highlightId ? '' : status, limit: String(TABLE_FETCH_CAP), doc_type: 'invoice', direction: 'outgoing' });
      if (effStart) params.set('start_date', effStart);
      if (effEnd) params.set('end_date', effEnd);
      if (highlightId) params.set('highlight_id', highlightId);
      return api(`/invoices?${params.toString()}`);
    },
  });

  const { data: customers } = useQuery({
    queryKey: ['customers-list-ar'],
    queryFn: () => api('/customers?limit=200'),
  });

  const { data: products } = useQuery({
    queryKey: ['products-list-ar'],
    queryFn: () => api('/products?limit=500'),
  });

  const createProductMut = useMutation({
    mutationFn: (body: any) => api('/products', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['products-list-ar'] }),
  });

  const { data: invoiceDetail } = useQuery({
    queryKey: ['invoice', viewId],
    queryFn: () => api(`/invoices/${viewId}`),
    enabled: !!viewId,
  });

  const createMut = useMutation({
    mutationFn: (body: any) => api('/invoices', { method: 'POST', body }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['invoices-ar'] }); setShowForm(false); },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api(`/invoices/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoices-ar'] }),
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api(`/invoices/${id}/status`, { method: 'PATCH', body: { status } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoices-ar'] }),
  });

  const confirmReceiptMatchMut = useMutation({
    mutationFn: (body: { receipt_id: string; invoice_id?: string; invoice_ids?: string[] }) =>
      api('/invoices/confirm-receipt-match', {
        method: 'POST',
        body: body.invoice_ids?.length ? { receipt_id: body.receipt_id, invoice_ids: body.invoice_ids } : { receipt_id: body.receipt_id, invoice_id: body.invoice_id },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoices-ar'] }),
    onError: (err: any) => toast.error(err?.error || err?.message || tr('Receipt match failed', '收據配對失敗', '收据配对失败')),
  });

  // Unified bank-transaction ↔ invoice match confirm (server posts GL + syncs file payment_status)
  const matchConfirmMut = useMutation({
    mutationFn: ({ txId, invoiceId, invoiceIds, varianceAmount, reviewReason }: {
      txId: string; invoiceId: string | null; invoiceIds?: string[];
      varianceAmount?: number; reviewReason?: string;
    }) =>
      api(`/bank-statements/transactions/${txId}/match`, {
        method: 'PATCH',
        body: {
          ...(invoiceIds?.length ? { invoice_ids: invoiceIds } : { invoice_id: invoiceId }),
          action: 'confirm',
          ...(varianceAmount !== undefined ? { variance_amount: varianceAmount } : {}),
          ...(reviewReason ? { review_reason: reviewReason } : {}),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices-ar'] });
      queryClient.invalidateQueries({ queryKey: ['entries'] });
      queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
    },
    onError: (err: any, vars) => {
      const reviewed = !vars.reviewReason && promptReviewedVariance(err);
      if (reviewed) {
        matchConfirmMut.mutate({ ...vars, ...reviewed });
        return;
      }
      toast.error(err?.error || err?.message || tr('Confirm failed', '確認失敗', '确认失败'));
    },
  });

  function addItem() {
    setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0, amount: 0 }] });
  }

  function updateItem(idx: number, field: string, value: any) {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    if (field === 'quantity' || field === 'unit_price') {
      items[idx].amount = items[idx].quantity * items[idx].unit_price;
    }
    setForm({ ...form, items });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const sub = form.items.reduce((s: number, i: any) => s + i.amount, 0);
    const tax = sub * ((form.tax_rate || 0) / 100);
    const disc = (form.discount_type === 'percent' ? sub * ((form.discount_value || 0) / 100) : (form.discount_value || 0));
    // Ensure outgoing direction for AR
    createMut.mutate({ ...form, direction: 'outgoing', total: sub + tax - disc, subtotal: sub, tax_amount: tax, discount_amount: disc });
  }

  const invoices = data?.data || [];
  const rows = invoices.map((inv: any) => ({ ...inv, _counterparty: inv.customer_name || '' }));
  const controls = useTableControls({ rows, columns: COLS, totalCount: data?.total });

  // Deep-linked invoice (banner / File Storage) → auto-open its detail modal,
  // then clear the ?highlight= param. The old effect guarded on
  // `invoices?.data` while `invoices` IS the array — always falsy → dead code.
  useEffect(() => {
    if (!highlightId || isLoading || !data) return;
    const inv = ((data.data || []) as any[]).find((i: any) => i.id === highlightId);
    if (inv) {
      setViewId(highlightId);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('highlight');
      setSearchParams(newParams, { replace: true });
    }
  }, [highlightId, data, isLoading, searchParams, setSearchParams]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{tr('Accounts Receivable (AR)', '應收帳款 AR', '应收账款 AR')}</h2>
          <p className="text-muted-foreground mt-1">{tr('Manage sales invoices and customer payments', '管理銷售發票和客戶付款', '管理销售发票和客户付款')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            // 2026-11-05: always the rules engine — the useAI/LLM-modal branch was
            // a dead-on toggle (no UI ever switched it) pointing at the weaker
            // /match/llm-analyze pipeline (0 vs 10 suggestions on Hillcrest).
            try {
              const result = await api('/invoices/auto-match-receipts?direction=outgoing', { method: 'POST' });
              if (result.matched?.length > 0) {
                setReceiptMatchResults(result.matched);
              } else {
                toast.info(tr('No receipt matches found', '沒有找到收據配對', '没有找到收据配对'));
              }
            } catch (e: any) { toast.error(e?.message || 'Match failed'); }
          }}
            className="flex items-center gap-1 px-3 py-2 border rounded-md text-sm hover:bg-muted">
            <Link2 className="h-4 w-4" /> {tr('Match Receipts', '配對收據', '配对收据')}
          </button>
          <button onClick={async () => {
            // 2026-11-05: always the rules engine (same as Bank Statements'
            // Match Invoices & Cards) — identical suggestions everywhere.
            try {
              const result = await api('/bank-statements/auto-match?direction=outgoing', { method: 'POST' });
              if (result.matched?.length > 0) {
                setBankMatchResults(result.matched);
              } else {
                toast.info(tr('No bank matches found', '沒有找到銀行配對', '没有找到银行配对'));
              }
            } catch (e: any) { toast.error(e?.message || 'Match failed'); }
          }}
            className="flex items-center gap-1 px-3 py-2 border rounded-md text-sm hover:bg-muted">
            <Zap className="h-4 w-4" /> {tr('Match Bank Deposits', '配對銀行存款', '配对银行存款')}
          </button>
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:opacity-90">
            <Plus className="h-4 w-4" /> {tr('Create Invoice', '建立發票', '建立发票')}
          </button>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); }}
            placeholder={tr('Search invoices...', '搜尋發票...', '搜索发票...')} className="w-full pl-10 pr-4 py-2 border rounded-md bg-background text-sm" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); }}
          className="px-3 py-2 border rounded-md bg-background text-sm">
          <option value="">{tr('All Status', '全部狀態', '全部状态')}</option>
          <option value="draft">{tr('Draft', '草稿', '草稿')}</option>
          <option value="sent">{tr('Receivable', '應收', '应收')}</option>
          <option value="paid">{tr('Paid', '已收', '已收')}</option>
          <option value="overdue">{tr('Overdue', '逾期未收', '逾期未收')}</option>
        </select>
      </div>

      {isLoading ? <div className="text-center py-12 text-muted-foreground">{tr('Loading...', '載入中...', '载入中...')}</div> :
       invoices.length === 0 ? <div className="text-center py-12 text-muted-foreground">{tr('No receivable records', '未有應收記錄', '未有应收记录')}</div> : (
        <div className="bg-card border rounded-xl overflow-hidden">
          {controls.filteredCount === 0 && (
            <div className="text-center py-8 text-muted-foreground border-b">
              {tr('No invoices match the filters', '沒有符合篩選的發票', '没有符合筛选的发票')}
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <Th controls={controls} colKey="invoice_number">{tr('Invoice No.', '發票號碼', '发票号码')}</Th>
                <Th controls={controls} colKey="_counterparty" className="hidden md:table-cell">{tr('Customer', '客戶', '客户')}</Th>
                <th className="text-left p-3 w-16">{tr('Type', '類型', '类型')}</th>
                <Th controls={controls} colKey="status">{tr('Status', '狀態', '状态')}</Th>
                <Th controls={controls} colKey="total" align="right" className="hidden lg:table-cell">{tr('Amount', '金額', '金额')}</Th>
                <Th controls={controls} colKey="issue_date" className="hidden lg:table-cell">{tr('Date', '日期', '日期')}</Th>
                <th className="text-left p-3 hidden lg:table-cell min-w-[180px]">{tr('Posting (Dr/Cr)', '過賬（借/貸）', '过账（借/贷）')}</th>
                <th className="text-right p-3">{tr('Actions', '操作', '操作')}</th>
              </tr>
            </thead>
            <tbody>
              {controls.visibleRows.map((inv: any) => (
                <React.Fragment key={inv.id}>
                  <tr id={`invoice-row-${inv.id}`} className={`border-b hover:bg-muted/30 ${highlightId === inv.id ? 'bg-yellow-100 dark:bg-yellow-900/30 ring-2 ring-yellow-400' : ''}`}>
                  <td className="p-3 font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      {inv.invoice_number}
                      <InvoiceNumberBadge source={inv.invoice_number_source} direction={inv.direction} counterpartyRef={inv.counterparty_ref} />
                      {inv.needs_review?.includes('direction') && (
                        <span title={tr('AI OCR could not determine if this is AR (you issued) or AP (you received). Please review.', 'AI OCR 無法判斷此為 AR（你開出）或 AP（你接收）。請審核。', 'AI OCR 无法判断此为 AR（你开出）或 AP（你接收）。请审核。')}>
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-900 dark:text-amber-100" />
                        </span>
                      )}
                      {inv.needs_review?.includes('company_not_detected') && (
                        <span title={tr('Your company name was not detected in this invoice. It may be between two third parties.', '未在此發票中檢測到你公司名稱。可能涉及兩個第三方。', '未在此发票中检测到你公司名称。可能涉及两个第三方。')}>
                          <Info className="h-3.5 w-3.5 text-blue-900 dark:text-blue-200" />
                        </span>
                      )}
                      {inv.needs_review?.includes('duplicate') && (
                        <span title={tr('An invoice with this number already existed. The number was adjusted to avoid conflict.', '此發票號碼已存在。號碼已調整以避免衝突。', '此发票号码已存在。号码已调整以避免冲突。')}>
                          <Copy className="h-3.5 w-3.5 text-orange-700 dark:text-orange-300" />
                        </span>
                      )}
                      {inv.status === 'sent' && !inv.linked_invoice_id && (
                        <span title={tr('Awaiting receipt — no payment receipt linked yet', '等待收款收據 — 尚未連結付款收據', '等待收款收据 — 尚未连结付款收据')}>
                          <Receipt className="h-3.5 w-3.5 text-red-900 dark:text-red-200" />
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="p-3 hidden md:table-cell">{inv.customer_name || '-'}</td>
                  <td className="p-3">
                    <Tooltip content={tr('Accounts Receivable — outgoing invoice to a customer', '應收帳款 — 你開給客戶的發票', '应收账款 — 你开给客户的发票')}>
                      <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-700">
                        {tr('AR', '應收', '应收')}
                      </span>
                    </Tooltip>
                  </td>
                  <td className="p-3"><LifecycleStepper type="document" variant="compact" stage={stageOf('document', { status: inv.status })} stageSince={inv.updated_at} /></td>
                  <td className="p-3 text-right hidden lg:table-cell">{inv.currency} {inv.total?.toLocaleString()}</td>
                  <td className="p-3 hidden lg:table-cell">{inv.issue_date}</td>
                  <td className="p-3 hidden lg:table-cell">
                    {inv.posting?.lines?.length ? (
                      <div className="flex flex-col items-start gap-0.5 cursor-pointer group" onClick={() => setPostingEditId(prev => (prev === inv.id ? null : inv.id))}>
                        {inv.posting.lines.map((l: any, i: number) => {
                          const side = l.debit > 0 ? 'Dr' : 'Cr';
                          return (
                            <span key={`${l.account_code}-${i}`}
                              className={`inline-flex items-center gap-1 max-w-[240px] text-[10px] font-mono px-1 py-px rounded group-hover:ring-1 group-hover:ring-primary/40 ${
                                isTemporaryAccount(l.account_name)
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                  : 'bg-primary/10 text-primary'
                              }`}
                              title={`${side} ${l.account_code} · ${l.account_name} · ${(l.debit > 0 ? l.debit : l.credit).toFixed(2)}${tr(' (click to edit posting)', '（點擊編輯分錄）', '（点击编辑分录）')}`}>
                              <span className={`text-[9px] font-semibold ${side === 'Dr' ? 'text-blue-600 dark:text-blue-400' : 'text-orange-900 dark:text-orange-200'}`}>{side}</span>
                              <span className="truncate">{l.account_code}<span className="text-muted-foreground ml-1">{l.account_name}</span></span>
                            </span>
                          );
                        })}
                        {inv.posting.lines.length > 2 && (
                          <span className="text-[9px] text-muted-foreground">
                            {inv.posting.entry_source === 'manual' ? tr('manual split', '手動拆分', '手动拆分') : tr('split', '拆分', '拆分')}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] text-muted-foreground italic">{tr('Not posted', '未過賬', '未过账')}</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setViewId(inv.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 border rounded text-xs hover:bg-muted"
                        title={tr('View details — full record, GL posting editor and the uploaded PDF', '查看明細——完整記錄、過賬分錄編輯及上傳文件', '查看明细——完整记录、过账分录编辑及上传文件')}
                      >
                        <FileText className="h-3.5 w-3.5" />
                        {tr('View details', '查看明細', '查看明细')}
                      </button>
                      <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={actionMenuId === inv.id}
                        aria-label={tr('More actions', '更多操作', '更多操作')}
                        title={tr('More actions', '更多操作', '更多操作')}
                        onClick={(event) => {
                          const rect = event.currentTarget.getBoundingClientRect();
                          setActionMenuPos({
                            top: Math.min(rect.bottom + 6, window.innerHeight - 300),
                            right: Math.max(8, window.innerWidth - rect.right),
                          });
                          setActionMenuId(prev => prev === inv.id ? null : inv.id);
                        }}
                        className="p-1.5 rounded hover:bg-muted"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </div>

                    {actionMenuId === inv.id && createPortal(
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setActionMenuId(null)} />
                        <div
                          role="menu"
                          aria-label={tr('Invoice actions', '發票操作', '发票操作')}
                          className="fixed z-50 w-60 overflow-hidden rounded-xl border bg-card p-1 shadow-lg"
                          style={{ top: `${actionMenuPos.top}px`, right: `${actionMenuPos.right}px` }}
                        >
                          {inv.file_id && (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setActionMenuId(null);
                                navigate(`/file-storage?highlight=${inv.file_id}`);
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
                            >
                              <CornerUpRight className="h-4 w-4" />
                              {tr('Open source document', '開啟來源文件', '打开来源文件')}
                            </button>
                          )}
                          <button
                            type="button"
                            role="menuitem"
                            data-testid="audit-trail-btn"
                            onClick={() => { setActionMenuId(null); setAuditId(inv.id); }}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
                          >
                            <History className="h-4 w-4" />
                            {tr('View audit trail', '查看審計追蹤', '查看审计追踪')}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => { setActionMenuId(null); navigate(`/invoices/review/${inv.id}`); }}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
                          >
                            <Pencil className="h-4 w-4" />
                            {tr('Edit invoice', '編輯發票', '编辑发票')}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => { setActionMenuId(null); downloadInvoicePDF(inv.id, inv.invoice_number); }}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
                          >
                            <Download className="h-4 w-4" />
                            {tr('Download PDF', '下載 PDF', '下载 PDF')}
                          </button>
                          {inv.status === 'draft' && (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => { setActionMenuId(null); updateStatus.mutate({ id: inv.id, status: 'sent' }); }}
                              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
                            >
                              {tr('Mark as sent', '標記為已發送', '标记为已发送')}
                            </button>
                          )}
                          {inv.status === 'sent' && (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => { setActionMenuId(null); updateStatus.mutate({ id: inv.id, status: 'paid' }); }}
                              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
                            >
                              {tr('Mark as paid', '標記為已收款', '标记为已收款')}
                            </button>
                          )}
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              if (!confirm(tr('Delete this invoice?', '確定刪除此發票?', '确定删除此发票?'))) return;
                              setActionMenuId(null);
                              deleteMut.mutate(inv.id);
                            }}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                            {tr('Delete invoice', '刪除發票', '删除发票')}
                          </button>
                        </div>
                      </>,
                      document.body
                    )}
                  </td>
                </tr>
                {inv.posting?.lines?.length ? (
                  <tr className="border-b">
                    <td colSpan={8} className="p-0">
                      <SlideOpen open={postingEditId === inv.id}>
                        <InvoicePostingEditor invoiceId={inv.id} direction={inv.direction} total={inv.total} posting={inv.posting} />
                      </SlideOpen>
                    </td>
                  </tr>
                ) : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <TablePager controls={controls} />
        </div>
      )}

      {/* Create Invoice Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="bg-card border rounded-xl p-6 w-full max-w-2xl mx-4 my-8 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg">{tr('Create Invoice', '建立發票', '建立发票')}</h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                    placeholder={tr("Invoice No. (auto if blank)", "發票號碼（留空自動產生）", "发票号码（留空自动产生）")} className="w-full px-3 py-2 border rounded-md bg-background text-sm" />
                  {!form.invoice_number && <p className="text-[10px] text-muted-foreground mt-0.5">{tr('Leave blank to auto-generate', '留空則根據設定格式自動產生號碼', '留空则根据设定格式自动产生号码')}</p>}
                </div>
                <select required value={form.customer_id} onChange={(e) => {
                  const cid = e.target.value;
                  const cust = (customers?.data || []).find((c: any) => c.id === cid);
                  setForm({
                    ...form, customer_id: cid,
                    attn: cust?.name || '', customer_phone: cust?.phone || '',
                    customer_email: cust?.email || '', customer_address: cust?.address || '',
                  });
                }}
                  className="px-3 py-2 border rounded-md bg-background text-sm">
                  <option value="">{tr('Select Customer *', '選擇客戶 *', '选择客户 *')}</option>
                  {(customers?.data || []).map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                  className="px-3 py-2 border rounded-md bg-background text-sm" />
                <input type="date" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="px-3 py-2 border rounded-md bg-background text-sm" placeholder={tr("Due date", "到期日", "到期日")} />
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="px-3 py-2 border rounded-md bg-background text-sm">
                  <option value="HKD">HKD</option><option value="USD">USD</option><option value="CNY">CNY</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={form.receipt_number} onChange={(e) => setForm({ ...form, receipt_number: e.target.value })}
                  placeholder={tr("Receipt No.", "收據號碼", "收据号码")} className="px-3 py-2 border rounded-md bg-background text-sm" />
                <input type="date" value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })}
                  className="px-3 py-2 border rounded-md bg-background text-sm" placeholder={tr("Payment date", "付款日期", "付款日期")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={form.attn} onChange={(e) => setForm({ ...form, attn: e.target.value })}
                  placeholder={tr("Attn Contact", "Attn 聯絡人", "Attn 联络人")} className="px-3 py-2 border rounded-md bg-background text-sm" />
                <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  placeholder={tr("Tel", "Tel 電話", "Tel 电话")} className="px-3 py-2 border rounded-md bg-background text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  placeholder={tr("E-mail", "E-mail 電郵", "E-mail 电邮")} className="px-3 py-2 border rounded-md bg-background text-sm" />
                <input value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
                  placeholder={tr("Address", "Address 地址", "Address 地址")} className="px-3 py-2 border rounded-md bg-background text-sm" />
              </div>

              <div className="border rounded-md p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{tr('Items', '項目 Items', '项目 Items')}</span>
                  <button type="button" onClick={addItem} className="text-xs text-primary hover:underline">+ {tr('Add Item', '新增項目', '新增项目')}</button>
                </div>
                {form.items.map((item, idx) => {
                  const searchText = productSearch[idx] || '';
                  const filteredProducts = (products?.data || []).filter((p: any) =>
                    !searchText || p.name.toLowerCase().includes(searchText.toLowerCase())
                  ).slice(0, 8);
                  const showDropdown = productDropdown === idx;
                  return (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center relative">
                    <div className="col-span-5 relative">
                      <input required value={item.description} onChange={(e) => {
                        updateItem(idx, 'description', e.target.value);
                        setProductSearch({ ...productSearch, [idx]: e.target.value });
                        setProductDropdown(idx);
                      }}
                        onFocus={() => { setProductSearch({ ...productSearch, [idx]: item.description }); setProductDropdown(idx); }}
                        onBlur={() => setTimeout(() => setProductDropdown(null), 200)}
                        placeholder={tr("Search product or enter description", "搜尋產品或輸入描述", "搜索产品或输入描述")} className="w-full px-2 py-1 border rounded text-sm" />
                      {showDropdown && (
                        <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-card border rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {filteredProducts.map((p: any) => (
                            <button key={p.id} type="button"
                              onMouseDown={() => {
                                updateItem(idx, 'description', p.name);
                                updateItem(idx, 'unit_price', p.unit_price || 0);
                                updateItem(idx, 'product_id', p.id);
                                setProductDropdown(null);
                              }}
                              className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted flex justify-between">
                              <span>{p.name}</span>
                              <span className="text-muted-foreground text-xs">{p.currency} {p.unit_price}</span>
                            </button>
                          ))}
                          {filteredProducts.length === 0 && searchText && (
                            <button type="button"
                              onMouseDown={() => {
                                const name = searchText.trim();
                                if (!name) return;
                                createProductMut.mutate({ name, unit_price: 0, currency: form.currency, category: 'Service' });
                                updateItem(idx, 'description', name);
                                setProductDropdown(null);
                              }}
                              className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted text-primary">
                              {tr('+ New product', '+ 新增產品', '+ 新增产品')}「{searchText}」
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <input type="number" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value))}
                      className="col-span-2 px-2 py-1 border rounded text-sm" placeholder={tr("Qty", "數量", "数量")} />
                    <input type="number" step="0.01" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value))}
                      className="col-span-2 px-2 py-1 border rounded text-sm" placeholder={tr("Unit Price", "單價", "单价")} />
                    <span className="col-span-2 text-sm text-right">{form.currency} {(item.amount || 0).toFixed(2)}</span>
                    <button type="button" onClick={() => { const items = form.items.filter((_, i) => i !== idx); setForm({ ...form, items: items.length ? items : [{ description: '', quantity: 1, unit_price: 0, amount: 0 }] }); }} className="col-span-1 text-destructive text-xs">✕</button>
                  </div>
                );})}
                <div className="flex items-center gap-1.5 justify-end text-xs text-muted-foreground pt-2 border-t">
                  <span>{tr('Tax %', '稅%', '税%')}</span>
                  <input type="number" min="0" max="100" step="0.5" value={form.tax_rate || 0}
                    onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })}
                    className="w-14 px-1 py-0.5 border rounded text-xs text-center bg-background" />
                  <span className="mr-2">{tr('Discount', '折扣', '折扣')}</span>
                  <input type="number" min="0" step="0.01" value={form.discount_value || ''}
                    onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value) || 0 })}
                    placeholder="0" className="w-18 px-1 py-0.5 border rounded text-xs text-center bg-background" />
                  <select value={form.discount_type}
                    onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    className="text-xs border rounded px-1 py-0.5 bg-background">
                    <option value="flat">$</option>
                    <option value="percent">%</option>
                  </select>
                </div>
                <div className="text-right font-bold text-sm pt-1">
                  {tr('Total', '總計', '总计')}: {form.currency} {(() => {
                    const sub = form.items.reduce((s: number, i: any) => s + i.amount, 0);
                    const tax = sub * ((form.tax_rate || 0) / 100);
                    const disc = (form.discount_type === 'percent' ? sub * ((form.discount_value || 0) / 100) : (form.discount_value || 0));
                    return (sub + tax - disc).toFixed(2);
                  })()}
                </div>
              </div>

              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={tr("Notes", "備註 Notes", "备注 Notes")} className="w-full px-3 py-2 border rounded-md bg-background text-sm" rows={2} />
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-md text-sm">{tr('Cancel', '取消', '取消')}</button>
                <button type="submit" disabled={createMut.isPending}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm">{tr('Create', '建立', '建立')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {viewId && invoiceDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setViewId(null)}>
          <div className="bg-card border rounded-xl p-6 w-[90vw] max-w-[90vw] h-[85vh] mx-4 flex gap-6" onClick={(e) => e.stopPropagation()}>
            {/* Left: details */}
            <div className="w-[45%] flex flex-col min-h-0 overflow-y-auto pr-2 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-lg inline-flex items-center gap-1.5">{tr('Invoice', '發票', '发票')} #{invoiceDetail.invoice_number}
                  <InvoiceNumberBadge source={invoiceDetail.invoice_number_source} direction={invoiceDetail.direction} counterpartyRef={invoiceDetail.counterparty_ref} />
                </h3>
                <button onClick={() => setViewId(null)} className="text-muted-foreground">✕</button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">{tr('Customer', '客戶', '客户')}:</span> {invoiceDetail.customer_name}</div>
                <div><span className="text-muted-foreground">{tr('Status', '狀態', '状态')}:</span> <LifecycleStepper type="document" variant="full" stage={stageOf('document', { status: invoiceDetail.status })} stageSince={invoiceDetail.updated_at} onOpen={() => setDrillOpen(true)} /></div>
                <div><span className="text-muted-foreground">{tr('Date', '日期', '日期')}:</span> {invoiceDetail.issue_date}</div>
                <div><span className="text-muted-foreground">{tr('Due', '到期', '到期')}:</span> {invoiceDetail.due_date}</div>
                {invoiceDetail.receipt_number && <div><span className="text-muted-foreground">{tr('Receipt No.', '收據號碼', '收据号码')}:</span> {invoiceDetail.receipt_number}</div>}
                {invoiceDetail.paid_date && <div><span className="text-muted-foreground">{tr('Payment Date', '付款日期', '付款日期')}:</span> {invoiceDetail.paid_date}</div>}
              </div>
              {/* Full record description: line items, linked bank transactions,
                  and the editable GL posting (multi-account split, balanced) */}
              <InvoiceDetailPanel invoiceId={invoiceDetail.id} />
              <LifecycleDrilldown type="invoice" id={invoiceDetail.id ?? viewId} open={drillOpen} onClose={() => setDrillOpen(false)} />
              <button
                onClick={() => downloadInvoicePDF(invoiceDetail.id, invoiceDetail.invoice_number)}
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                <Download className="h-4 w-4" /> {tr('Download PDF', '下載 PDF', '下载 PDF')}
              </button>
            </div>
            {/* Right: the ACTUAL uploaded document. Bundle-split invoices deep-link
                to their source page (#page=N is honoured by the browser PDF viewer). */}
            <div className="flex-1 border rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
              {invoiceDetail.file_id ? (
                <iframe
                  src={`${WORKER_API_BASE}/file-storage/${invoiceDetail.file_id}/download?inline=1&token=${localStorage.getItem('token') || ''}${iframeClientParam()}${invoiceDetail.source_page ? `#page=${invoiceDetail.source_page}` : ''}`}
                  className="w-full h-full border-0"
                  title="Uploaded Document"
                />
              ) : (
                <div className="text-center text-muted-foreground p-8">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">{tr('No uploaded document', '沒有上傳的文件', '没有上传的文件')}</p>
                  <p className="text-xs mt-1">{tr('This invoice was created manually.', '此發票為手動建立。', '此发票为手动建立。')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bank Match Review Modal (unified engine) */}
      {bankMatchResults && (
        <AutoMatchReviewModal
          matches={bankMatchResults}
          onConfirm={(txId, invoiceId, invoiceIds) => matchConfirmMut.mutateAsync({ txId, invoiceId, invoiceIds })}
          onReject={() => Promise.resolve() /* suggest-only: nothing persisted to unlink */}
          onClose={() => {
            setBankMatchResults(null);
            queryClient.invalidateQueries({ queryKey: ['invoices-ar'] });
          }}
        />
      )}

      {/* Receipt Match Review Modal */}
      {receiptMatchResults && (
        <ReceiptMatchReviewModal
          matches={receiptMatchResults}
          onConfirm={(receiptId, invoiceIds) => {
            confirmReceiptMatchMut.mutate({ receipt_id: receiptId, invoice_ids: invoiceIds });
          }}
          onClose={() => {
            setReceiptMatchResults(null);
            queryClient.invalidateQueries({ queryKey: ['invoices-ar'] });
          }}
        />
      )}

      {/* LLM Match Modal */}
      {showLLMModal && (
        <LLMMatchModal
          type={llmModalType}
          direction={llmModalType === 'receipt-invoice' ? 'outgoing' : undefined}
          onConfirm={(txId, invoiceId, invoiceIds) => {
            if (llmModalType === 'bank-invoice' && txId) {
              return matchConfirmMut.mutateAsync({ txId, invoiceId: invoiceId || '', invoiceIds });
            } else if (llmModalType === 'receipt-invoice' && invoiceId) {
              return confirmReceiptMatchMut.mutateAsync({ receipt_id: txId || '', invoice_ids: invoiceIds || [invoiceId] });
            }
            return Promise.resolve();
          }}
          onReject={() => Promise.resolve()}
          onClose={() => {
            setShowLLMModal(false);
            queryClient.invalidateQueries({ queryKey: ['invoices-ar'] });
          }}
        />
      )}

      {/* Audit Trail Modal */}
      <AuditTrailModal open={!!auditId} onClose={() => setAuditId(null)} invoiceId={auditId} />
    </div>
  );
}
